// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useCallback, FC, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, useThemeValue, IconButton, Svg, Asset } from '@altermaind/web/ui';
import { ErrorCodeApi } from '@/_shared/constants/errors';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useBreakingPoints } from '@/_shared/helpers/useBreakpoint';
import { useDialog, useNotification, useResponsiveStyle } from '@/_shared/hooks';
import { getEmployerCreditsCall } from '@/credits/state/api/getEmployerCreditsCall';
import { getContentDetailsCall } from '@/training/hardSkills/state/api';
import { postQuizStartCall } from '../../state/api/postQuizStartCall';
import { StartQuizHeaderSkeleton } from './StartQuizHeaderSkeleton';
import { StartQuizSkeleton } from './StartQuizSkeleton';
import { DialogStartQuizPayload } from './types';

dayjs.extend(duration);

export const DialogStartQuiz: FC = () => {
  const t = useTranslations('quiz.startDialog');
  const tGeneric = useTranslations('trainingHardSkills.cards.GENERIC');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { execute: executePostQuizStart, loading: loadingPostQuizStart } = postQuizStartCall.hook();
  const {
    execute: executeGetContentDetails,
    loading: loadingGetContentDetails,
    error: errorGetContentDetails,
    data,
  } = getContentDetailsCall.hook();
  const { execute: executeGetEmployerCredits } = getEmployerCreditsCall.hook();
  const navigate = useNavigate();
  const { closeDialog, Dialog, isOpen, payload } = useDialog<DialogStartQuizPayload>('quizStarter');
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();
  const gapM = convertSizeToNumber(`${useThemeValue('sizes', 'm')}`);
  const primaryColor = useThemeValue('colors', 'primary');
  const primaryBackgroundColor = useThemeValue('colors', 'primary/background');

  const quizId = useMemo(() => payload?.quizId || '', [payload?.quizId]);
  const isSkillToBoost = useMemo(() => payload?.isSkillToBoost || false, [payload?.isSkillToBoost]);

  const inProgress = useMemo(
    () => data?.userDetails?.progress?.startedAt && !data?.userDetails?.progress?.completedAt,
    [data?.userDetails],
  );

  const isCompleted = useMemo(
    () => data?.userDetails?.progress?.startedAt && data?.userDetails?.progress?.completedAt,
    [data?.userDetails],
  );

  const textButton = useMemo(() => {
    if (inProgress) {
      return t('resume');
    }
    if (isCompleted) {
      return t('restart');
    }
    return t('start');
  }, [inProgress, isCompleted, t]);

  const iconButton = useMemo(() => {
    if (inProgress) {
      return 'emptyPlay';
    }
    if (isCompleted) {
      return 'refresh';
    }
    return 'emptyPlay';
  }, [inProgress, isCompleted]);

  const buttonClickHandler = useCallback(() => {
    if (quizId) {
      executePostQuizStart({
        quizId,
        successSaga: ({ response }) => {
          if (response?.id) {
            closeDialog();
            if (data?.credits) {
              executeGetEmployerCredits({});
            }
            navigate(Routes.Quiz.replace(':quizId', quizId).replace(':submissionId', response.id));
          }
        },
        errorSaga: ({ error }) => {
          if (error.type === ErrorCodeApi.CREDITS_NOT_AVAILABLE) {
            notify(t('errors.noCredits'), 'error');
            return;
          }
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    }
  }, [
    quizId,
    executePostQuizStart,
    closeDialog,
    data?.credits,
    navigate,
    executeGetEmployerCredits,
    notify,
    tShared,
    t,
  ]);

  const QuizInfoContent = useMemo(() => {
    return loadingGetContentDetails ? (
      <StartQuizHeaderSkeleton />
    ) : (
      <Box
        display='flex'
        mx='l'
        flexDirection='column'
        px='s'
        width='100%'
        zIndex={99}
        justifyContent='flex-end'
        height='100%'
      >
        <Box mb='xs' textAlign='left'>
          <Typography variant='p2/light'>
            {data?.tags?.map(tag => tag.label.toLocaleUpperCase()).join(', ')}
          </Typography>
          <Typography variant='h1'>{t('title', { quizName: data?.title })}</Typography>
        </Box>
        <Box
          display='flex'
          flexWrap='wrap'
          justifyContent={genericResponsiveValue({
            breakPoints: { desktop: 'center' },
            defaultValue: 'left',
          })}
          alignItems='stretch'
          style={{ gap: gapM }}
          marginRight={genericResponsiveValue({
            breakPoints: { mobile: '0' },
            defaultValue: 'auto',
          })}
          mb='l'
        >
          {/* DURATION */}
          {/* <Box display='flex' flexDirection='row' alignItems='center'>
            <Svg name='clock' fill='primary/text' size='xs' />
            <Typography variant='p2' ml='xxs' color='primary/text'>
              {t('duration', {
                time: dayjs
                  .duration(userDetails?.durationInMinutes ?? 0, 'minutes')
                  .asMinutes(),
              })}
            </Typography>
          </Box> */}

          {/* QUESTIONS */}
          <Box display='flex' flexDirection='row' alignItems='center'>
            <Svg name='document' fill='primary/text' size='xs' />
            <Typography variant='p2' ml='xxs' color='primary/text'>
              {t('questions', { questions: data?.questionsCount })}
            </Typography>
          </Box>

          {/* RATING (se presenti) */}
          {/* <Box display='flex' flexDirection='row' alignItems='center'>
                <Svg name='star' stroke='primary/text' size='xs' />
                <Typography variant='p2' ml='xxs' color='primary/text'>
                  {4.6}
                </Typography>
              </Box> */}

          {/* CREDITS (se presenti) */}
          {data?.credits ? (
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Svg name='wallet' stroke='primary/text' size='xs' />
              <Typography variant='p2' color='primary/text' ml='xxs'>
                {t('credits', { credits: data?.credits })}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }, [
    loadingGetContentDetails,
    data?.tags,
    data?.title,
    data?.questionsCount,
    data?.credits,
    t,
    genericResponsiveValue,
    gapM,
  ]);

  const Header = useMemo(() => {
    return (
      <Box
        p='xs'
        display='flex'
        borderRadius='m'
        borderBottomLeftRadius={0}
        borderBottomRightRadius={0}
        alignItems='center'
        flexDirection='row'
        padding='none'
        pt='s'
        height={250}
        background={`linear-gradient(
          to bottom,
          ${primaryColor},
          transparent
        ),
        conic-gradient(from 0.725turn at 90% 35%, ${primaryColor} 1rad, ${primaryBackgroundColor} 4rad)`}
        position='relative'
      >
        {isDesktop ? QuizInfoContent : null}
        <Box style={{ mixBlendMode: 'luminosity' }} marginLeft='auto' marginRight='xxl'>
          <Asset width={160} domain='images' name='doubleBoxQuestions' />
        </Box>
        <Box
          position='absolute'
          left={0}
          bottom={0}
          width='100%'
          height='100%'
          backgroundColor='primary'
          opacity={0.3}
        />
      </Box>
    );
  }, [primaryColor, primaryBackgroundColor, isDesktop, QuizInfoContent]);

  useEffect(() => {
    if (quizId) {
      executeGetContentDetails({ contentId: quizId });
    }
  }, [quizId, executeGetContentDetails]);

  return (
    <Dialog
      header={Header}
      headerStyle={{ mt: 'none', mb: 'none' }}
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      closeButton
      specialsComponentsInHeader={
        <>
          {/* Skill to boost badge - top left */}
          {!loadingGetContentDetails && isSkillToBoost && !inProgress && !isCompleted ? (
            <Box
              ml={genericResponsiveValue({
                breakPoints: {
                  desktop: 'm',
                  tablet: 'xxs',
                },
                defaultValue: 'none',
              })}
              px='xxs'
              py='xxxs'
              borderRadius='xl'
              alignItems='center'
              display='flex'
              flexDirection='row'
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
            >
              <Svg fill='white' name='rocket' size='xs' />
              <Typography color='white' variant='p3/light' ml='xxxs' fontSize='xxs'>
                {tGeneric('skillToBoost')}
              </Typography>
            </Box>
          ) : null}
        </>
      }
    >
      {!isDesktop ? QuizInfoContent : null}
      <Box display='flex' mx='l' flexDirection='column' px='s' width='100%'>
        {loadingGetContentDetails ? (
          <StartQuizSkeleton />
        ) : (
          <>
            <Typography variant='p2/light'>{data?.summary}</Typography>
            {/* line between row rowIndex and rowIndex+1 (not after the last one) */}
            {data && data?.views > 0 && (
              <Typography variant='p3/light' color='primary/text' mt='m'>
                {t(`colleaguesHint.${data?.views === 1 ? 'singular' : 'plural'}`, {
                  colleagues: data?.views || 0,
                })}
              </Typography>
            )}
          </>
        )}

        <IconButton
          variant='primary'
          onClick={buttonClickHandler}
          mt='l'
          mr='auto'
          data-testid='confirm-operation-button'
          loading={loadingPostQuizStart || loadingGetContentDetails}
          disabled={loadingPostQuizStart || loadingGetContentDetails || !!errorGetContentDetails}
          prefixSvg={iconButton}
          svgStyle={{
            size: 'xs',
          }}
          textStyle={{
            paddingLeft: 'xxs',
          }}
          spinnerColor='primary/text'
        >
          {textButton}
        </IconButton>
      </Box>
    </Dialog>
  );
};
